const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.centroautomotrizhlfrd.com.do/api';

export interface Holiday {
  id: string;
  date: string; // YYYY-MM-DD format
  name: string;
  type: 'holiday' | 'non_working_day';
  description?: string;
}

export interface WorkingDaysConfig {
  workingDays: number[]; // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  holidays: Holiday[];
  workingHours?: Record<number, { start: string; end: string } | null>;
}

// Formatea fecha local a YYYY-MM-DD sin sesgo de zona horaria
const formatDateLocal = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Obtiene la configuración de días laborales y festivos desde el backend
 */
export const getWorkingDaysConfig = async (): Promise<WorkingDaysConfig> => {
  try {
    const response = await fetch(`${API_BASE_URL}/working-days`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Error al obtener configuración de días laborales');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching working days config:', error);
    // Fallback sin mocks: todos los días laborables y sin festivos
    return {
      workingDays: [0, 1, 2, 3, 4, 5, 6],
      holidays: []
    };
  }
};

/**
 * Verifica si una fecha es un día laboral disponible
 */
export const isWorkingDay = (date: Date, config: WorkingDaysConfig): boolean => {
  const dayOfWeek = date.getDay();
  
  // Verificar si es un día de la semana laboral
  if (!config.workingDays.includes(dayOfWeek)) {
    return false;
  }

  // Verificar que exista horario definido para ese día (si el backend lo envía)
  if (config.workingHours) {
    const hours = config.workingHours[dayOfWeek];
    if (!hours || !hours.start || !hours.end) {
      return false;
    }
  }

  // Verificar si es un día festivo
  const dateString = formatDateLocal(date);
  const isHoliday = config.holidays.some(holiday => holiday.date === dateString);
  
  return !isHoliday;
};

/**
 * Obtiene la lista de fechas disponibles para citas
 */
export const getAvailableDates = (config: WorkingDaysConfig, daysAhead: number = 30): Array<{value: string, label: string}> => {
  const dates = [];
  const today = new Date();
  
  for (let i = 1; i <= daysAhead; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    
    if (isWorkingDay(date, config)) {
      dates.push({
        value: formatDateLocal(date),
        label: date.toLocaleDateString('es-ES', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: 'America/Santo_Domingo'
        })
      });
    }
  }
  
  return dates;
};
